import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Star, BarChart3, ThumbsUp } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewCard from "@/components/ReviewCard";
import QualityBadge from "@/components/QualityBadge";
import { supabase } from "@/integrations/supabase/client";
import { mockReviews, type Review } from "@/lib/mockData";

const PreceptorPage = () => {
  const [searchParams] = useSearchParams();
  const preceptorName = searchParams.get("name") || "";
  const [dbReviews, setDbReviews] = useState<Review[]>([]);

  useEffect(() => {
    const fetchPreceptorReviews = async () => {
      const { data: preceptorRows } = await supabase
        .from("preceptors")
        .select("*, reviews(id, type, title, organization, location, rating, difficulty, would_do_again, review, author, tags, thumbs_up, thumbs_down, created_at, major, start_date, end_date)")
        .eq("name", preceptorName) as any;

      if (preceptorRows && preceptorRows.length > 0) {
        const mapped: Review[] = preceptorRows
          .filter((p: any) => p.reviews)
          .map((p: any) => {
            const r = p.reviews;
            return {
              id: r.id,
              type: r.type,
              title: r.title,
              organization: r.organization,
              location: r.location,
              rating: Number(r.rating),
              difficulty: Number(r.difficulty),
              wouldDoAgain: r.would_do_again ?? true,
              review: r.review,
              author: r.author,
              date: r.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
              tags: r.tags || [],
              thumbsUp: r.thumbs_up || 0,
              thumbsDown: r.thumbs_down || 0,
              userId: null,
              isOwner: !!(r as any).is_owner,
            };
          });
        setDbReviews(mapped);
      }
    };

    if (preceptorName) fetchPreceptorReviews();
  }, [preceptorName]);

  const mockMatches = useMemo(() => {
    return mockReviews.filter(
      (r) => r.preceptors?.some((p) => p.name === preceptorName)
    );
  }, [preceptorName]);

  const allReviews = useMemo(() => {
    const dbIds = new Set(dbReviews.map((r) => r.id));
    return [...dbReviews, ...mockMatches.filter((r) => !dbIds.has(r.id))];
  }, [dbReviews, mockMatches]);

  const preceptorStats = useMemo(() => {
    let totalRating = 0;
    let totalDifficulty = 0;
    let totalWouldTakeAgain = 0;
    let count = 0;

    mockReviews.forEach((r) => {
      r.preceptors?.forEach((p) => {
        if (p.name === preceptorName) {
          totalRating += p.rating;
          totalDifficulty += p.difficulty;
          totalWouldTakeAgain += p.wouldTakeAgain;
          count++;
        }
      });
    });

    if (count === 0) {
      return { rating: 0, difficulty: 0, wouldTakeAgain: 0, specialty: "Unknown" };
    }

    const specialty = (() => {
      for (const r of mockReviews) {
        const p = r.preceptors?.find((p) => p.name === preceptorName);
        if (p) return p.specialty;
      }
      return "Unknown";
    })();

    return {
      rating: totalRating / count,
      difficulty: totalDifficulty / count,
      wouldTakeAgain: Math.round(totalWouldTakeAgain / count),
      specialty,
    };
  }, [preceptorName]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Preceptor hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-transparent" />
        <div className="container relative py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex items-start gap-6">
              <QualityBadge rating={preceptorStats.rating} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Preceptor</span>
                </div>
                <h1 className="text-3xl font-serif font-bold text-foreground mb-1">
                  {preceptorName}
                </h1>
                <p className="text-muted-foreground">{preceptorStats.specialty}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Stats grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-10"
        >
          <div className="rounded-xl border border-border bg-card p-5 text-center shadow-card">
            <Star className="w-5 h-5 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{preceptorStats.rating.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Avg Rating</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 text-center shadow-card">
            <BarChart3 className="w-5 h-5 text-accent mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{preceptorStats.difficulty.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Difficulty</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 text-center shadow-card">
            <ThumbsUp className="w-5 h-5 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{preceptorStats.wouldTakeAgain}%</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Would Take Again</p>
          </div>
        </motion.div>

        {/* Reviews */}
        <h2 className="text-lg font-serif font-semibold text-foreground mb-4">
          Reviews ({allReviews.length})
        </h2>

        {allReviews.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <User className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">
              No reviews found for this preceptor.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {allReviews.map((review, i) => (
              <ReviewCard key={review.id} review={review} index={i} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PreceptorPage;
